import { useEffect, useRef } from "react";
import { usePickerAnnouncement } from "./PickerAnnouncement";
import { usePickerRoot } from "./PickerContext";
import { PickerItem } from "./PickerItem";
import { usePickerListElementRef } from "./PickerListElementContext";
import { PickerLoading } from "./PickerParts";
import styles from "./Picker.module.scss";

const LOADING_MORE_LABEL = "Loading more";
const LOAD_MORE_ERROR_LABEL = "Couldn't load more";
const RETRY_LABEL = `${LOAD_MORE_ERROR_LABEL}. Try again`;

/** The failed-page action row; the surface announces the failure while it shows. */
const PickerLoadMoreRetry = ({ onRetry }: { onRetry: () => void }) => {
    usePickerAnnouncement(LOAD_MORE_ERROR_LABEL, "assertive");

    return (
        <PickerItem destructive closeOnSelect={false} onSelect={onRetry}>
            {RETRY_LABEL}
        </PickerItem>
    );
};

/**
 * The end of a paged list: an invisible marker that asks the root for the
 * next page (`onLoadMore`) as it scrolls into view of `Picker.List`, a
 * "Loading more" row, and after a failed page a "Couldn't load more. Try
 * again" action row that asks again when pressed. Renders nothing once there
 * is nothing more to load. Composed roots place it after their last row,
 * before `Picker.Footer`; data-driven roots render it themselves.
 */
export const PickerLoadMore = () => {
    const { hasMore, loadMoreStatus, onLoadMore } = usePickerRoot();
    const listRef = usePickerListElementRef();
    const markerRef = useRef<HTMLDivElement>(null);

    // Observing again whenever the status returns to idle delivers a fresh
    // entry, so a settled page that leaves the marker in view asks for the
    // next one. A failed page only retries from its action row.
    useEffect(() => {
        const marker = markerRef.current;
        if (marker === null || !hasMore || loadMoreStatus !== "idle") {
            return;
        }
        const observer = new IntersectionObserver(
            (entries) => {
                if (entries.at(-1)?.isIntersecting === true) {
                    onLoadMore();
                }
            },
            { root: listRef?.current ?? null, rootMargin: "100px" },
        );
        observer.observe(marker);
        return () => observer.disconnect();
    }, [hasMore, loadMoreStatus, onLoadMore, listRef]);

    if (!hasMore && loadMoreStatus === "idle") {
        return null;
    }

    return (
        <>
            {hasMore && (
                <div
                    ref={markerRef}
                    aria-hidden
                    className={styles.loadMoreMarker}
                />
            )}
            {loadMoreStatus === "loading" && (
                <PickerLoading>{LOADING_MORE_LABEL}</PickerLoading>
            )}
            {loadMoreStatus === "error" && (
                <PickerLoadMoreRetry onRetry={onLoadMore} />
            )}
        </>
    );
};
